import type { StatusResponse } from "../api.js";

/** A one-line summary of what the pipeline is doing, for the overview screen. */
export function pipelineSummary(status: StatusResponse | null): { text: string; progress: number } | null {
  if (!status) return null;
  if (status.status !== "processing" && status.status !== "uploaded") return null;

  const labels: Record<string, string> = {
    uploaded: "Reading the recording",
    "audio-extracted": "Extracting audio",
    transcribed: "Transcribing",
    "script-generated": "Writing the script",
    "actions-detected": "Finding the moments that matter",
    "voice-generated": "Generating narration",
    "timeline-built": "Building the timeline",
    ready: "Finishing up",
  };

  return {
    text: labels[status.step ?? ""] ?? "Processing",
    progress: status.progress ?? 0,
  };
}

/**
 * Whether the overview should stand aside and open the editor.
 *
 * Here rather than inline in the screen so that it is one rule with one test,
 * and so that adding a project status forces a decision about it. Every status
 * has to be claimed by something: the pipeline summary, the recorder prompt, a
 * failure, or this. A status claimed by none of them renders an empty page
 * under a status pill, which is what `rendering` did — the summary covers
 * `processing` and `uploaded`, this covered `ready` and `completed`, and a
 * project that was merely rendering could not be opened at all.
 *
 * `rendering` belongs here. A render works from a snapshot taken when it was
 * queued, so the project stays editable while one runs, and the nav bar reports
 * the progress on every screen anyway.
 *
 * Never for a launch video, and never before the kind is known. A launch video
 * finishes as `completed` like anything else, and handing it over opened an
 * editor onto a timeline that does not exist — whose preview then asked for a
 * proxy of a recording that does not exist either, and that job's failure
 * marked the finished video `failed`. Unknown is treated as "not yet": the
 * status arrives with the project, and guessing before it has would repeat the
 * same trip for the one request where the guess was wrong.
 */
export function handsOverToEditor(
  status: string | null | undefined,
  kind: "demo" | "launch" | null | undefined = "demo",
): boolean {
  if (kind !== "demo") return false;
  return status === "ready" || status === "completed" || status === "rendering";
}
